import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Check,
  Lock,
  Bell,
  Film,
  LayoutGrid,
  Heart,
  ClipboardList,
  Footprints,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { Logo } from "@/components/brand/Logo";
import { ViewfinderFrame } from "@/components/brand/ViewfinderFrame";
import { MotionChevrons } from "@/components/brand/MotionChevrons";
import { createFoundingClubLead } from "@/lib/api";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import "../landing.css";

/**
 * Marketing landing page at `/` — feedback-first revision.
 *
 * Ported from the Claude Design handoff (redesign/landing-ds). The thesis is
 * the two-sided coaching loop: a coach turns a recorded match — or a tactical
 * board when there's no footage — into player-by-player feedback, and the
 * player responds. Three interactive demos carry it: the hero capture-mode
 * switcher, the send→respond→closed loop, and the multi-team switcher.
 *
 * Layout resolves entirely against the design tokens (see ../landing.css);
 * shared brand/UI components are reused from the app rather than the design
 * bundle. The privacy explorer + founding-club form are kept from the prior
 * landing (youth-footage privacy is the #1 buyer objection; the form is the
 * beta funnel) and satisfy the public-multi-team e2e contract.
 *
 * Brand rules honored: lowercase brand moments, cyan = "act here" (ink on
 * cyan, never white), viewfinder + O·X·dotted-cyan vocabulary, no emoji.
 */

const HERO_IMG = "/hero-match.jpg";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LandingPage() {
  useDocumentMeta({
    title: "tktic — coaching feedback, delivered",
    description:
      "tktic turns recorded matches — or a tactical board when there's no footage — into focused, player-by-player feedback. Coaches, players, and parents, working the same loop across every team.",
    ogType: "website",
  });

  return (
    <main data-testid="landing-page" className="landing">
      <BetaStrip />
      <Hero />
      <LoopSection />
      <CaptureSection />
      <LiveSection />
      <TeamsSection />
      <AudienceSection />
      <StepsSection />
      <Privacy />
      <Join />
      <Footer />
    </main>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────────

function Eyebrow({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <span className="eyebrow" style={style}>
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  lead,
  center,
  id,
}: {
  kicker: React.ReactNode;
  title: React.ReactNode;
  lead?: React.ReactNode;
  center?: boolean;
  id?: string;
}) {
  return (
    <div className={"sec-head" + (center ? " sec-head--center" : "")}>
      <Eyebrow>{kicker}</Eyebrow>
      <h2 id={id} className="sec-title">
        {title}
      </h2>
      {lead && <p className="sec-lead">{lead}</p>}
    </div>
  );
}

// ── Beta strip ──────────────────────────────────────────────────────────────

function BetaStrip() {
  return (
    <div className="beta">
      <div className="beta-in">
        <span aria-hidden="true" className="beta-dot" />
        <span className="beta-text">
          now onboarding founding clubs
          <span className="beta-text-tail"> · limited spots</span>
        </span>
        <a href="#join" className="beta-link">
          claim yours ›
        </a>
      </div>
    </div>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="hero" id="top" data-testid="landing-hero">
      <div className="hero-in">
        <div className="hero-copy reveal">
          <Eyebrow>COACHING FEEDBACK PLATFORM</Eyebrow>
          <h1 data-testid="landing-hero-headline" className="hero-title">
            coach feedback,
            <br />
            <span className="rule-under">delivered.</span>
          </h1>
          <p data-testid="landing-hero-subhead" className="hero-sub">
            tktic turns recorded matches — or a tactical board when there's no
            footage — into focused, player-by-player feedback. coaches, players,
            and parents, working the same loop across every team.
          </p>
          <div data-testid="landing-hero-ctas" className="hero-cta">
            <Button asChild variant="primary" size="lg">
              <a href="#join" data-testid="landing-cta-hub">
                set up your team{" "}
                <span aria-hidden="true" className="ml-1 tracking-tighter">
                  ›››
                </span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#how" data-testid="landing-cta-how">
                see how it works
              </a>
            </Button>
          </div>
          <ul className="hero-trust">
            {[
              "recorded match review",
              "tactical boards",
              "live streaming",
              "multi-team",
            ].map((t) => (
              <li key={t}>
                <Check className="text-accent" size={14} strokeWidth={2.6} />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="hero-preview reveal reveal--d1">
          <CapturePreview />
        </div>
      </div>
    </section>
  );
}

// ── Hero capture preview: recorded / tactical / live mode switcher ──────────

const CAPTURE_MODES = [
  { id: "recorded", label: "Recorded match", tc: "PLAY 04 · 23:14" },
  { id: "tactical", label: "Tactical board", tc: "SET PIECE · 02" },
  { id: "live", label: "Live stream", tc: "LIVE · 67:12" },
] as const;
type CaptureMode = (typeof CAPTURE_MODES)[number]["id"];

function CapturePreview() {
  const [mode, setMode] = useState<CaptureMode>("recorded");
  const [drawKey, setDrawKey] = useState(1);
  const active = CAPTURE_MODES.find((m) => m.id === mode)!;

  // Re-trigger the draw-in animation whenever the mode changes.
  useEffect(() => {
    setDrawKey((k) => k + 1);
  }, [mode]);

  const isLive = mode === "live";
  const isBoard = mode === "tactical";

  return (
    <div style={{ width: "100%", maxWidth: 520 }}>
      <div
        className="seg"
        role="tablist"
        aria-label="Capture mode"
        style={{ marginBottom: 12 }}
      >
        {CAPTURE_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={mode === m.id}
            className="seg-btn"
            data-active={mode === m.id}
            onClick={() => setMode(m.id)}
          >
            {m.id === "recorded" && <Film size={14} strokeWidth={2} />}
            {m.id === "tactical" && <LayoutGrid size={14} strokeWidth={2} />}
            {m.id === "live" && <span className="seg-dot" />}
            {m.label}
          </button>
        ))}
      </div>

      <div data-theme="dark" className="capture-card">
        <div className="capture-stage">
          {isBoard ? (
            <img src="/pitch-broadcast.svg" alt="" className="capture-bg" />
          ) : (
            <img src={HERO_IMG} alt="match footage" className="capture-bg" />
          )}
          <div className="capture-scrim" />

          {isBoard ? (
            <BoardTelestration key={drawKey} live />
          ) : isLive ? null : (
            <MatchTelestration key={drawKey} live />
          )}

          {isLive && (
            <div className="capture-live">
              <span className="seg-dot" /> LIVE
              <span className="capture-live-sep">·</span>
              <span style={{ opacity: 0.8 }}>Riverside Field · cam 1</span>
            </div>
          )}

          <ViewfinderFrame
            label={active.tc}
            color="#ffffff"
            style={{
              position: "absolute",
              inset: 0,
              filter: "drop-shadow(0 0 2px rgba(0,0,0,.55))",
            }}
          />
        </div>

        <div className="capture-bar">
          <span className="capture-bar-meta">
            RIVERSIDE FC · U14 ·{" "}
            {isBoard ? "TACTICAL BOARD" : isLive ? "LIVE FEED" : "1ST HALF"}
          </span>
          <span className="capture-bar-cta">
            <span
              className="seg-dot"
              style={{
                background: "var(--accent)",
                boxShadow: "0 0 0 3px rgba(34,211,238,.25)",
              }}
            />
            mark this moment
          </span>
        </div>
      </div>

      <p className="capture-caption">
        {mode === "recorded" &&
          "Upload any match video — draw the run, circle the moment, send it to the player."}
        {mode === "tactical" &&
          "No footage? Coach the idea on the tactical board with the same drawing tools."}
        {mode === "live" &&
          "Supported camera setups stream live — families watch, you clip moments as they happen."}
      </p>
    </div>
  );
}

// Telestration overlays. Cyan = our players + pass line, white = run arrow,
// orange = the defender (X) to beat. Base state is fully drawn so reduced
// motion / print still shows the complete play.
function MatchTelestration({ live }: { live?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 160 100"
      preserveAspectRatio="none"
      className={"tele " + (live ? "tele--on" : "")}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        filter: "drop-shadow(0 0 1.2px rgba(0,0,0,.75))",
      }}
    >
      <defs>
        <marker
          id="run-arrow"
          viewBox="0 0 10 10"
          refX="7"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M0 0L10 5L0 10z" fill="#ffffff" />
        </marker>
      </defs>
      <circle
        className="tele-pop"
        cx="40"
        cy="62"
        r="9"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
      />
      <circle
        className="tele-pop tele-d2"
        cx="118"
        cy="34"
        r="9"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
      />
      <g
        className="tele-pop tele-d1"
        stroke="var(--flag)"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <line x1="82" y1="40" x2="94" y2="52" />
        <line x1="94" y1="40" x2="82" y2="52" />
      </g>
      <path
        className="tele-draw tele-d3"
        d="M49 58 Q 86 40 110 36"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeDasharray="2 4"
        strokeLinecap="round"
        pathLength={100}
      />
      <path
        className="tele-draw tele-d4"
        d="M106 50 Q 112 40 116 26"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        markerEnd="url(#run-arrow)"
        pathLength={100}
      />
    </svg>
  );
}

function BoardTelestration({ live }: { live?: boolean }) {
  const ours: [number, number][] = [
    [30, 70],
    [34, 40],
    [64, 84],
    [70, 30],
    [92, 58],
    [120, 40],
  ];
  const theirs: [number, number][] = [
    [82, 46],
    [104, 70],
    [128, 64],
  ];
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 160 100"
      preserveAspectRatio="none"
      className={"tele " + (live ? "tele--on" : "")}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      <defs>
        <marker
          id="board-arrow"
          viewBox="0 0 10 10"
          refX="7"
          refY="5"
          markerWidth="5.5"
          markerHeight="5.5"
          orient="auto-start-reverse"
        >
          <path d="M0 0L10 5L0 10z" fill="var(--accent)" />
        </marker>
      </defs>
      {ours.map(([x, y], i) => (
        <g
          key={i}
          className="tele-pop"
          style={{ animationDelay: i * 70 + "ms" }}
        >
          <circle cx={x} cy={y} r="6" fill="var(--accent)" />
          <text
            x={x}
            y={y + 2.6}
            textAnchor="middle"
            fontSize="6"
            fontWeight="700"
            fill="var(--accent-ink)"
            fontFamily="JetBrains Mono"
          >
            {i + 4}
          </text>
        </g>
      ))}
      {theirs.map(([x, y], i) => (
        <g
          key={i}
          className="tele-pop tele-d2"
          stroke="var(--flag)"
          strokeWidth="2"
          strokeLinecap="round"
          style={{ animationDelay: 200 + i * 70 + "ms" }}
        >
          <line x1={x - 4} y1={y - 4} x2={x + 4} y2={y + 4} />
          <line x1={x + 4} y1={y - 4} x2={x - 4} y2={y + 4} />
        </g>
      ))}
      <path
        className="tele-draw tele-d3"
        d="M70 30 Q 96 30 118 40"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeLinecap="round"
        markerEnd="url(#board-arrow)"
        pathLength={100}
      />
      <path
        className="tele-draw tele-d4"
        d="M64 84 Q 88 78 92 60"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2"
        strokeDasharray="2 4"
        strokeLinecap="round"
        pathLength={100}
      />
    </svg>
  );
}

// ── The loop ────────────────────────────────────────────────────────────────

type LoopResponse = "got" | "help" | "tried";

function LoopSection() {
  return (
    <section className="band" id="how">
      <div className="wrap">
        <SectionHead
          id="landing-how-heading"
          kicker="THE TWO-SIDED LOOP"
          title={
            <>
              coach gives. <span className="rule-under">player responds.</span>
            </>
          }
          lead="Every piece of feedback is a conversation, not a broadcast. The coach marks the moment; the player watches, then answers — got it, need help, or i tried this. You both see the loop close."
        />
        <LoopDemo />
      </div>
    </section>
  );
}

function LoopDemo() {
  const [sent, setSent] = useState(false);
  const [response, setResponse] = useState<LoopResponse | null>(null);
  const reset = () => {
    setSent(false);
    setResponse(null);
  };

  return (
    <div className="loop-grid">
      {/* Coach side */}
      <Card padded className="loop-card">
        <div className="loop-head">
          <span className="eyebrow">COACH · ALEX MERCER</span>
          <Badge variant="team">GIVES</Badge>
        </div>
        <div className="loop-frame" data-theme="dark">
          <img src={HERO_IMG} alt="" className="capture-bg" />
          <div className="capture-scrim" />
          <svg
            viewBox="0 0 160 100"
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <circle cx="44" cy="60" r="9" fill="none" stroke="var(--accent)" strokeWidth="2" />
            <path
              d="M53 56 Q 90 40 116 38"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="2"
              strokeDasharray="2 4"
              strokeLinecap="round"
            />
            <circle cx="120" cy="36" r="9" fill="none" stroke="#fff" strokeWidth="2" />
          </svg>
          <ViewfinderFrame
            label="PLAY 04 · 23:14"
            color="#fff"
            style={{ position: "absolute", inset: 0 }}
          />
        </div>
        <div className="loop-note">
          <div className="loop-note-title">Switch the play earlier</div>
          <p className="loop-note-body">
            When you get it here, your head's down. Pick the pass to the back
            post — it's on a beat sooner. Watch the run.
          </p>
        </div>
        <div className="loop-actions">
          {!sent ? (
            <Button variant="primary" size="default" onClick={() => setSent(true)}>
              send to player <span aria-hidden="true">›››</span>
            </Button>
          ) : (
            <span className="loop-status loop-status--sent">
              <Check size={14} strokeWidth={2.4} /> shared with player
            </span>
          )}
        </div>
      </Card>

      {/* Connector */}
      <div className="loop-link" aria-hidden="true">
        <span className={"loop-link-line " + (sent ? "is-live" : "")} />
        <span className={"loop-link-dot " + (sent ? "is-live" : "")} />
        <span className={"loop-link-line " + (response ? "is-live" : "")} />
      </div>

      {/* Player side */}
      <Card padded className={"loop-card " + (sent ? "" : "loop-card--dim")}>
        <div className="loop-head">
          <span className="eyebrow">PLAYER · SAM (#9)</span>
          <Badge variant={response ? "accent" : "muted"}>
            {response ? "RESPONDED" : "RESPONDS"}
          </Badge>
        </div>

        {!sent ? (
          <div className="loop-empty">
            <span className="loop-empty-glyph">
              <Bell size={20} strokeWidth={1.8} />
            </span>
            <p>
              New feedback from your coach will land here — with the exact moment,
              marked.
            </p>
          </div>
        ) : (
          <>
            <div className="loop-inbox">
              <span className="eyebrow" style={{ color: "var(--accent)" }}>
                FOR YOU · TODAY
              </span>
              <div className="loop-note-title" style={{ marginTop: 6 }}>
                Switch the play earlier
              </div>
              <p className="loop-note-body">
                When you get it here, your head's down. Pick the pass to the back
                post…
              </p>
            </div>
            <div className="loop-respond">
              <span className="loop-respond-label">your response</span>
              <ResponseChips value={response} onChange={setResponse} />
            </div>
            {response && (
              <div className="loop-closed">
                <span className="loop-status loop-status--closed">
                  <Check size={14} strokeWidth={2.4} /> loop closed — coach
                  notified
                </span>
                <button type="button" className="loop-reset" onClick={reset}>
                  run it again
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

// Landing-local response chips (a marketing demo — not the real feedback flow).
const RESPONSES: { id: LoopResponse; label: string }[] = [
  { id: "got", label: "got it ✓" },
  { id: "help", label: "need help ?" },
  { id: "tried", label: "i tried this ⚡" },
];

function ResponseChips({
  value,
  onChange,
}: {
  value: LoopResponse | null;
  onChange: (v: LoopResponse) => void;
}) {
  return (
    <div className="rchips" role="group" aria-label="Your response">
      {RESPONSES.map((r) => (
        <button
          key={r.id}
          type="button"
          className="rchip"
          data-kind={r.id}
          data-active={value === r.id}
          aria-pressed={value === r.id}
          onClick={() => onChange(r.id)}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

// ── Capture modes (capabilities) ────────────────────────────────────────────

function CaptureSection() {
  const items = [
    {
      tag: "RECORDED MATCH",
      title: "Telestrate the real moment",
      body: "Upload match video from any camera or phone. Scrub to the play, draw the run, circle the decision — then assign the clip to one player or the whole squad.",
      img: "/landing/coach-review-telestrator.png",
    },
    {
      tag: "NO FOOTAGE? NO PROBLEM",
      title: "Coach it on the tactical board",
      body: "When you couldn't film, draw the idea instead. The same pitch, the same pens — set pieces, shape, movement — saved as feedback that lands the same way.",
      img: "/landing/tactical-board.png",
    },
  ];

  return (
    <section className="band band--elevated" id="capture">
      <div className="wrap">
        <SectionHead
          kicker="HOW YOU CAPTURE IT"
          title="film it, or draw it."
          lead="Coaching shouldn't wait on a camera. tktic gives you the same feedback tools whether you have full match footage or a whiteboard idea after training."
        />
        <div className="cap-grid">
          {items.map((it) => (
            <Card key={it.tag} padded={false} className="cap-card">
              <div className="cap-shot" data-theme="dark">
                <img src={it.img} alt={it.title} />
              </div>
              <div className="cap-body">
                <Badge variant="muted">{it.tag}</Badge>
                <h3 className="cap-title">{it.title}</h3>
                <p className="cap-text">{it.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Live streaming ──────────────────────────────────────────────────────────

function LiveSection() {
  return (
    <section className="band" id="live" data-theme="dark">
      <div className="wrap live-in">
        <div className="live-copy">
          <Eyebrow style={{ color: "var(--accent)" }}>LIVE STREAMING</Eyebrow>
          <h2 className="sec-title">families never miss a match.</h2>
          <p className="sec-lead" style={{ marginLeft: 0 }}>
            Teams with a supported camera setup stream straight to tktic. Parents
            watch live from a single link — no app.
          </p>
          <ul className="live-list">
            <li>
              <span className="dot-mini" /> Auto-recorded — the stream becomes
              your match film
            </li>
            <li>
              <span className="dot-mini" /> One private link per team, shared
              with families
            </li>
          </ul>
          <p className="live-public">
            Teams can make a season public — anyone can{" "}
            <Link to="/teams">browse public teams</Link> and watch their matches,
            no account needed.
          </p>
          <div className="live-cta">
            <Button asChild variant="primary" size="default">
              <a href="#join">check camera support ›</a>
            </Button>
            <span className="live-note">works with RTMP-supported setups</span>
          </div>
        </div>
        <div className="live-preview">
          <div className="capture-card">
            <div className="capture-stage">
              <img src={HERO_IMG} alt="live match" className="capture-bg" />
              <div className="capture-scrim" />
              <div className="capture-live">
                <span className="seg-dot" /> LIVE
                <span className="capture-live-sep">·</span>
                <span style={{ opacity: 0.8 }}>134 watching</span>
              </div>
              <ViewfinderFrame
                label="LIVE · 67:12"
                color="#fff"
                style={{ position: "absolute", inset: 0 }}
              />
            </div>
            <div className="capture-bar">
              <span className="capture-bar-meta">
                RIVERSIDE FC vs LAKESIDE · 2ND HALF
              </span>
              <span className="capture-bar-cta">
                <span
                  className="seg-dot"
                  style={{ background: "var(--accent)" }}
                />{" "}
                clip this
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Multi-team ──────────────────────────────────────────────────────────────

const TEAMS = [
  {
    id: "u14",
    name: "Riverside U14",
    div: "Boys · Div 1",
    color: "#22d3ee",
    kpis: [
      ["12", "MATCHES", "this season"],
      ["38", "CLIPS SENT", "last 30 days"],
      ["91%", "REVIEWED", "squad average"],
    ],
    roster: ["AM", "SB", "JT", "KP", "LO", "DR", "+13"],
  },
  {
    id: "u16",
    name: "Riverside U16",
    div: "Girls · Div 1",
    color: "#2da66e",
    kpis: [
      ["9", "MATCHES", "this season"],
      ["24", "CLIPS SENT", "last 30 days"],
      ["84%", "REVIEWED", "squad average"],
    ],
    roster: ["EC", "NH", "FA", "MM", "TS", "+14"],
  },
  {
    id: "u12",
    name: "Lakeside U12",
    div: "Mixed · Academy",
    color: "#ff8a3d",
    kpis: [
      ["7", "MATCHES", "this season"],
      ["16", "CLIPS SENT", "last 30 days"],
      ["77%", "REVIEWED", "squad average"],
    ],
    roster: ["RB", "CD", "PW", "JN", "+12"],
  },
] as const;

function TeamsSection() {
  return (
    <section className="band band--elevated" id="teams">
      <div className="wrap">
        <SectionHead
          kicker="MULTI-TEAM SUPPORT"
          title={
            <>
              one account. <span className="rule-under">every squad.</span>
            </>
          }
          lead="Run an academy or a club? Switch between teams in a tap. Each squad keeps its own roster, matches, and feedback — you keep one place to coach them all."
        />
        <MultiTeamDemo />
      </div>
    </section>
  );
}

function MultiTeamDemo() {
  const [team, setTeam] = useState<(typeof TEAMS)[number]["id"]>("u14");
  const t = TEAMS.find((x) => x.id === team)!;

  return (
    <div className="mt">
      <div className="mt-switch" role="tablist" aria-label="Your teams">
        {TEAMS.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={team === x.id}
            className="mt-team"
            data-active={team === x.id}
            onClick={() => setTeam(x.id)}
          >
            <span className="mt-crest" style={{ background: x.color }}>
              {x.name.split(" ").pop()}
            </span>
            <span className="mt-team-meta">
              <span className="mt-team-name">{x.name}</span>
              <span className="mt-team-div">{x.div}</span>
            </span>
          </button>
        ))}
        <button type="button" className="mt-team mt-team--add" disabled>
          <span className="mt-crest mt-crest--add">+</span>
          <span className="mt-team-meta">
            <span className="mt-team-name">Add a team</span>
            <span className="mt-team-div">unlimited</span>
          </span>
        </button>
      </div>

      <div className="mt-panel" key={t.id}>
        <div className="mt-kpis">
          {t.kpis.map(([v, l, h]) => (
            <div key={l} className="kpi">
              <span className="kpi-value">{v}</span>
              <span className="kpi-label">{l}</span>
              <span className="kpi-hint">{h}</span>
            </div>
          ))}
        </div>
        <div className="mt-roster">
          <span className="eyebrow">SQUAD · {t.name.toUpperCase()}</span>
          <div className="mt-avatars">
            {t.roster.map((rr, i) => (
              <span
                key={i}
                className={"mt-av " + (rr.startsWith("+") ? "mt-av--more" : "")}
                style={!rr.startsWith("+") ? { borderColor: t.color } : undefined}
              >
                {rr}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Audiences ───────────────────────────────────────────────────────────────

function AudienceSection() {
  const cards = [
    {
      role: "coaches",
      body: "Capture the play, mark the moment, send it by Tuesday. Roster, clips, tactical boards, and squad triage in one workspace.",
      glyph: <ClipboardList size={26} strokeWidth={1.9} />,
      primary: true,
    },
    {
      role: "players",
      body: "See exactly what your coach flagged, watch the moment, and answer back. Your feedback, your progress, your goals.",
      glyph: <Footprints size={26} strokeWidth={1.9} />,
    },
    {
      role: "parents",
      body: "Follow every match — live or replay — and see the coaching your child receives. No app, just a link.",
      glyph: <Heart size={26} strokeWidth={1.9} />,
    },
  ];

  return (
    <section className="band" data-theme="dark">
      <div className="wrap">
        <SectionHead
          kicker="BUILT FOR THE TOUCHLINE"
          title="everyone in the loop."
          center
        />
        <div className="aud-grid">
          {cards.map((c) => (
            <Card
              key={c.role}
              padded
              className={"aud-card " + (c.primary ? "aud-card--primary" : "")}
            >
              <span
                className={"aud-glyph " + (c.primary ? "aud-glyph--primary" : "")}
              >
                {c.glyph}
              </span>
              <h3 className="aud-role">{c.role}</h3>
              <p className="aud-body">{c.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── How it works steps ──────────────────────────────────────────────────────

function StepsSection() {
  const steps = [
    {
      n: "01",
      t: "capture",
      b: "Stream live, upload the match, or open a tactical board — whatever you've got.",
    },
    {
      n: "02",
      t: "mark the moment",
      b: "Scrub to the play, draw it, voice the lesson, and tag the player it's for.",
    },
    {
      n: "03",
      t: "close the loop",
      b: "The player watches and responds. You see who's got it and who needs you.",
    },
  ];

  return (
    <section className="band steps-band">
      <div className="wrap">
        <div className="steps-grid">
          {steps.map((s) => (
            <div key={s.n} className="step">
              <span className="step-n">{s.n}</span>
              <h3 className="step-t">{s.t}</h3>
              <p className="step-b">{s.b}</p>
            </div>
          ))}
        </div>
        <div className="steps-chev">
          <MotionChevrons count={6} width={220} height={48} color="var(--ink-faint)" />
        </div>
      </div>
    </section>
  );
}

// ── Privacy explorer (kept — buyer-trust funnel + e2e contract) ─────────────

type PrivacyRole = "player" | "guardian" | "coach" | "public";

const PRIVACY: Record<
  PrivacyRole,
  { label: string; sees: string[]; cant: string[] }
> = {
  player: {
    label: "a player",
    sees: [
      "clips & notes their coach sent them",
      "their own goals and progress",
      "their team's matches",
    ],
    cant: ["other players' private feedback", "anything from other clubs"],
  },
  guardian: {
    label: "a guardian",
    sees: [
      "everything sent to their child",
      "their child's team matches, live + replay",
    ],
    cant: ["other families' feedback", "coach-only tactical notes"],
  },
  coach: {
    label: "the coach",
    sees: [
      "the full match library for their team",
      "every note, clip and goal they've created",
      "who has watched what",
    ],
    cant: ["other teams' footage in the club", "teams they don't coach"],
  },
  public: {
    label: "the public",
    sees: [
      "only what a club explicitly makes public",
      "a public team's match stream, if enabled",
    ],
    cant: ["any player feedback — ever", "private or unlisted teams"],
  },
};

function Privacy() {
  const [role, setRole] = useState<PrivacyRole>("player");
  const r = PRIVACY[role];
  return (
    <section className="band" id="privacy" data-testid="landing-privacy">
      <div className="wrap">
        <SectionHead
          kicker="PRIVACY FIRST"
          title="it's kids' footage. we treat it that way."
          lead="Nothing is public unless your club deliberately makes it so. See exactly what each person can — and can't — reach."
        />
        <div className="priv-tabs" role="tablist" aria-label="role">
          {(Object.keys(PRIVACY) as PrivacyRole[]).map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={role === k}
              data-active={role === k}
              data-testid={`landing-privacy-tab-${k}`}
              className="priv-tab"
              onClick={() => setRole(k)}
            >
              {PRIVACY[k].label}
            </button>
          ))}
        </div>
        <div className="priv-panel" key={role}>
          <span className="eyebrow">{r.label} sees</span>
          <ul className="priv-list">
            {r.sees.map((s) => (
              <li key={s}>
                <Check className="priv-yes" size={16} strokeWidth={2.4} />
                {s}
              </li>
            ))}
          </ul>
          <span className="eyebrow" style={{ marginTop: 18, display: "block" }}>
            never sees
          </span>
          <ul className="priv-list">
            {r.cant.map((s) => (
              <li key={s}>
                <Lock className="priv-no" size={14} strokeWidth={2.2} />
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ── Founding-club form (kept — beta funnel + e2e contract) ──────────────────

function Join() {
  const [club, setClub] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<"" | "club" | "email" | "submit">("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const cleanClub = club.trim();
    if (!cleanClub) return setErr("club");
    const cleanEmail = email.trim();
    if (!EMAIL_RE.test(cleanEmail)) return setErr("email");
    setErr("");
    setPending(true);
    try {
      await createFoundingClubLead({
        club_name: cleanClub,
        email: cleanEmail,
        source: "landing_join",
        intent: "start_team",
      });
      setClub(cleanClub);
      setEmail(cleanEmail);
      setDone(true);
    } catch {
      setErr("submit");
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="join" id="join" data-testid="landing-join" data-theme="dark">
      <div className="wrap join-in">
        <Eyebrow style={{ color: "var(--accent)" }}>FOUNDING CLUBS · 2026</Eyebrow>
        <h2 className="join-title">bring tktic to your club.</h2>
        <p className="join-sub">
          We're onboarding a handful of clubs by hand this season — free, with
          setup help on a call. You shape what we build next.
        </p>

        {done ? (
          <p data-testid="landing-join-success" className="join-success" aria-live="polite">
            <Check size={18} strokeWidth={2.4} /> You're on the list,{" "}
            {club.trim().toLowerCase()} — we'll reach out at {email}.
          </p>
        ) : (
          <form onSubmit={submit} noValidate data-testid="landing-join-form" className="join-form">
            <input
              className="join-input"
              value={club}
              disabled={pending}
              onChange={(e) => {
                setClub(e.target.value);
                setErr("");
              }}
              placeholder="club or team name"
              aria-label="club or team name"
              aria-invalid={err === "club"}
            />
            <input
              className="join-input"
              type="email"
              value={email}
              disabled={pending}
              onChange={(e) => {
                setEmail(e.target.value);
                setErr("");
              }}
              placeholder="email"
              aria-label="email"
              aria-invalid={err === "email"}
            />
            <Button type="submit" variant="primary" size="default" disabled={pending}>
              {pending ? "sending..." : "start your team"}{" "}
              <span aria-hidden="true" className="ml-1">
                ›
              </span>
            </Button>
            {err && (
              <p className="join-note" role="alert" style={{ flexBasis: "100%" }}>
                {err === "club"
                  ? "tell us your club's name"
                  : err === "email"
                    ? "enter a valid email"
                    : "we couldn't add you just now. try again."}
              </p>
            )}
          </form>
        )}
        <p className="join-note">free through the beta · setup help included</p>
      </div>
    </section>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="ftr" data-testid="landing-footer">
      <div className="wrap ftr-in">
        <div className="ftr-brand">
          <Logo size={22} />
          <span className="ftr-tag">tactical coaching · built for the touchline</span>
        </div>
        <nav className="ftr-links">
          <Link to="/teams">Browse teams</Link>
          <a href="#how">How it works</a>
          <a href="#capture">Capture</a>
          <a href="#teams">For teams</a>
          <a href="#live">Live</a>
        </nav>
        <span data-testid="landing-footer-brand" className="ftr-copy">
          © 2026 tktic
        </span>
      </div>
    </footer>
  );
}
